<template>
	<Modal
		:show="showRenewPolicyModal"
		content_class="col-12 col-md-8 col-lg-7 col-xl-6"
	>
		<template #content>
			<div
				class="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50"
			>
				<h1 class="text-2xl font-semibold text-gray-900">
					Policy Renewal
				</h1>
				<button
					class="text-gray-500 hover:text-gray-700 transition-colors"
					@click="$store.commit('showRenewPolicyModal', false)"
					aria-label="Close"
				>
					<i class="fa fa-times text-lg"></i>
				</button>
			</div>

			<div
				v-if="selectedProduct"
				v-for="product in selectedProduct"
				:key="product.policyNumber"
				class="p-6 bg-white rounded-lg shadow-sm"
			>
				<h2 class="text-xl font-semibold text-gray-900 mb-6">
					Policy Number: {{ product.policyNumber }}
				</h2>

				<div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
					<p class="text-sm text-gray-600">
						<strong>Policy Type:</strong> {{ product.policyType }}
					</p>
					<p class="text-sm text-gray-600">
						<strong>Status:</strong> {{ product.policyStatus }}
					</p>
					<p class="text-sm text-gray-600">
						<strong>Channel:</strong> {{ product.channel }}
					</p>
					<p class="text-sm text-gray-600">
						<strong>Currency:</strong>
						{{ product.quotationCurrency }}
					</p>
				</div>

				<div v-if="product.quotationPremium" class="mb-6">
					<h3 class="text-lg font-medium text-gray-900 mb-3">
						Premium Details
					</h3>
					<div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
						<p class="text-sm text-gray-600">
							<strong>Base Premium:</strong>
							{{
								formatCurrency(
									product.quotationPremium.basePremium,
								)
							}}
						</p>
						<p class="text-sm text-gray-600">
							<strong>Total Premium:</strong>
							{{
								formatCurrency(
									product.quotationPremium.totalPremium,
								)
							}}
						</p>
					</div>
				</div>

				<!-- Renewal Form -->
				<div>
					<!-- Start Date -->
					<div
						class="bg-white/90 backdrop-blur-md rounded-2xl p-6 border border-gray-100 shadow-lg mb-6"
					>
						<div class="flex items-center gap-2 mb-4">
							<svg
								class="w-5 h-5 text-indigo-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
								/>
							</svg>
							<h3 class="text-lg font-semibold text-gray-900">
								Policy Start Date
							</h3>
						</div>
						<input
							id="start_date"
							type="date"
							v-model="start_date"
							@blur="validateField('start_date')"
							:min="todayDate"
							class="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
							:class="{ 'border-red-500': errors.start_date }"
						/>
						<p
							v-if="errors.start_date"
							class="text-sm text-red-500 mt-2"
						>
							{{ errors.start_date }}
						</p>
					</div>

					<!-- Remarks -->
					<div
						class="bg-white/90 backdrop-blur-md rounded-2xl p-6 border border-gray-100 shadow-lg mb-6"
					>
						<div class="flex items-center gap-2 mb-4">
							<svg
								class="w-5 h-5 text-indigo-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
								/>
							</svg>
							<h3 class="text-lg font-semibold text-gray-900">
								Remarks
							</h3>
						</div>
						<textarea
							v-model="remarks"
							@blur="validateField('remarks')"
							class="w-full h-32 px-4 py-3 rounded-xl border border-gray-200 bg-white/50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all resize-none"
							:class="{ 'border-red-500': errors.remarks }"
							placeholder="Please provide details about your query or issue..."
						></textarea>
						<p
							v-if="errors.remarks"
							class="text-sm text-red-500 mt-2"
						>
							{{ errors.remarks }}
						</p>
					</div>
				</div>

				<!-- Buttons -->
				<button
					v-if="!requestMade"
					class="w-full bg-indigo-600 text-white py-3 px-4 rounded-md hover:bg-indigo-700 transition-colors mt-6"
					@click="initiatePayment"
					:disabled="loading"
				>
					{{ loading ? 'Processing...' : 'Renew Policy' }}
				</button>

				<button
					v-if="requestMade"
					class="w-full bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 transition-colors mt-6"
					@click="acceptRenewal"
					:disabled="loading"
				>
					{{ loading ? 'Processing...' : 'Accept Renewal' }}
				</button>

				<button
					v-if="requestMade"
					class="w-full bg-red-600 text-white py-3 px-4 rounded-md hover:bg-red-700 transition-colors mt-6"
					@click="rejectRenewal"
					:disabled="loading"
				>
					Reject Renewal
				</button>
			</div>
		</template>
	</Modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Modal from './modal';
import {
	makeRenewalRequest,
	acceptRenewalRequest,
	rejectRenewalRequest,
	FetchInvoiceDetails,
} from '../../api/portal';

export default {
	components: { Modal },
	props: {
		selectedProduct: {
			type: Array,
			required: true,
		},
	},
	data() {
		return {
			start_date: '',
			remarks: '',
			requestMade: false,
			policySystemRef: '',
			errors: {
				start_date: '',
				remarks: '',
			},
		};
	},
	computed: {
		...mapGetters(['loading', 'showRenewPolicyModal']),
		todayDate() {
			const today = new Date();
			return today.toISOString().split('T')[0];
		},
	},
	methods: {
		...mapActions('invoices', [
			'setInvoiceDetails',
			'setClientInvoicesData',
		]),

		validateField(field) {
			this.errors[field] = '';
			if (field === 'start_date' && !this.start_date) {
				this.errors.start_date = 'Start date is required';
			}
			if (field === 'remarks' && !this.remarks) {
				this.errors.remarks = 'Remarks are required';
			}
		},

		validateAllFields() {
			['start_date', 'remarks'].forEach(this.validateField);
			return !Object.values(this.errors).some((error) => error !== '');
		},

		formatCurrency(amount) {
			if (!amount) return 'KES 0.00';
			return `KES ${Number(amount).toLocaleString(undefined, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			})}`;
		},

		async initiatePayment() {
			if (!this.validateAllFields()) {
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Form Validation Error',
					message:
						'Please correct the highlighted errors before submitting.',
					duration: 4000,
				});
				return;
			}
			this.$store.commit('loading', true);
			try {
				const paymentData = {
					policySystemRef: this.selectedProduct[0].policySystemRef,
					renewalStartDate: this.start_date,
					renewalRemarks: this.remarks,
				};

				await new Promise((resolve) => setTimeout(resolve, 1500));
				const res = await makeRenewalRequest(paymentData);
				if (res.status) {
					this.policySystemRef = res.data.policyRenewalSystemRef;
					this.requestMade = true;
					Event.$emit('notifications:show', {
						type: 'success',
						title: 'Success',
						message: 'Please accept renewal to proceed',
						duration: 3000,
					});
				}
			} catch (err) {
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message: err.message || 'Failed to process payment',
					duration: 3000,
				});
			} finally {
				this.$store.commit('loading', false);
			}
		},

		async acceptRenewal() {
			this.$store.commit('loading', true);
			try {
				await new Promise((resolve) => setTimeout(resolve, 1500));
				const res = await acceptRenewalRequest({
					policyRenewalSystemRef: this.policySystemRef,
				});
				if (res.status) {
					const ref = { invoiceSystemRef: res.data.invoiceSystemRef };
					const response = await FetchInvoiceDetails(ref);
					if (response.status) {
						this.setInvoiceDetails(response.data);
						this.$store.commit('showRenewPolicyModal', false);
						this.$store.commit('showInvoice', true); // make sure this mutation exists
						Event.$emit('notifications:show', {
							type: 'success',
							title: 'Success',
							message: 'Proceed to payment',
							duration: 3000,
						});
					}
				}
			} catch (err) {
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message: err.message || 'Failed to process payment',
					duration: 3000,
				});
			} finally {
				this.$store.commit('loading', false);
			}
		},

		async rejectRenewal() {
			this.$store.commit('loading', true);
			try {
				await new Promise((resolve) => setTimeout(resolve, 1500));
				const res = await rejectRenewalRequest({
					policyRenewalSystemRef: this.policySystemRef,
				});
				if (res.status) {
					Event.$emit('notifications:show', {
						type: 'success',
						title: 'Success',
						message: 'Renewal rejected',
						duration: 3000,
					});
					this.$store.commit('showRenewPolicyModal', false);
					this.requestMade = false;
				}
			} catch (err) {
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message: err.message || 'Failed to process payment',
					duration: 3000,
				});
			} finally {
				this.$store.commit('loading', false);
			}
		},
	},
};
</script>
